import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  HomeIcon, 
  UsersIcon, 
  UserIcon, 
  CalendarIcon, 
  CreditCardIcon,
  SettingsIcon,
  MenuIcon,
  LogOutIcon,
  ShieldIcon,
  RefreshCw,
  X,
  BellIcon,
  ChevronDownIcon,
  PillIcon,
  TestTubeIcon,
  FileTextIcon,
  HeartIcon,
  StethoscopeIcon,
  AlertTriangleIcon,
  SyringeIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { useSettings } from '../../contexts/SettingsContext';
import { authApi } from '../../lib/api';
import NotificationsDropdown from './NotificationsDropdown';
import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';

const Layout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  const { user } = useSelector((state: RootState) => state.auth);
  const { hospitalName, hospitalLogo, initialized } = useSettings();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Don't render layout on login page
  if (location.pathname === '/login') {
    return <Outlet />;
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use fallback name while settings are loading
  const displayName = initialized ? hospitalName : 'HMS Hospital';

  // Define navigation items with proper role-based access
  const navigation = useMemo(() => [
    { 
      name: 'Dashboard', 
      href: '/dashboard', 
      icon: HomeIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST', 'BILLING_STAFF', 'LAB_TECHNICIAN'] 
    },
    { 
      name: 'My Appointments', 
      href: '/appointments', 
      icon: CalendarIcon, 
      roles: ['DOCTOR'] 
    },
    { 
      name: 'My Patients', 
      href: '/patients', 
      icon: UsersIcon, 
      roles: ['DOCTOR'] 
    },
    { 
      name: 'My Schedule', 
      href: '/doctors/schedule', 
      icon: CalendarIcon, 
      roles: ['DOCTOR'] 
    },
    { 
      name: 'Patients', 
      href: '/patients', 
      icon: UsersIcon, 
      roles: ['ADMIN', 'NURSE', 'RECEPTIONIST'] 
    },
    { 
      name: 'Doctors', 
      href: '/doctors', 
      icon: UserIcon, 
      roles: ['ADMIN', 'RECEPTIONIST'] 
    },
    { 
      name: 'Appointments', 
      href: '/appointments', 
      icon: CalendarIcon, 
      roles: ['ADMIN', 'NURSE', 'RECEPTIONIST'] 
    },
    // Medical Management Modules
    { 
      name: 'Visits', 
      href: '/visits', 
      icon: HeartIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE'] 
    },
    { 
      name: 'Medications', 
      href: '/medications', 
      icon: PillIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE', 'PHARMACIST'] 
    },
    { 
      name: 'Lab Orders', 
      href: '/lab-orders', 
      icon: TestTubeIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE', 'LAB_TECHNICIAN'] 
    },
    { 
      name: 'Care Plans', 
      href: '/care-plans', 
      icon: FileTextIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE'] 
    },
    { 
      name: 'Clinical Notes', 
      href: '/clinical-notes', 
      icon: StethoscopeIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE'] 
    },
    { 
      name: 'Allergies', 
      href: '/allergies', 
      icon: AlertTriangleIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE'] 
    },
    { 
      name: 'Immunizations', 
      href: '/immunizations', 
      icon: SyringeIcon, 
      roles: ['ADMIN', 'DOCTOR', 'NURSE'] 
    },
    { 
      name: 'Billing', 
      href: '/billing', 
      icon: CreditCardIcon, 
      roles: ['ADMIN', 'BILLING_STAFF'] 
    },
    { 
      name: 'Users', 
      href: '/users', 
      icon: ShieldIcon, 
      roles: ['ADMIN'] 
    },
    { 
      name: 'Settings', 
      href: '/settings', 
      icon: SettingsIcon, 
      roles: ['ADMIN', 'DOCTOR'] 
    },
  ], []);

  // Filter navigation based on user role with proper type checking
  const filteredNavigation = useMemo(() => {
    if (!user?.role) return [];
    return navigation.filter(item => item.roles.includes(user.role));
  }, [user?.role, navigation]);

  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
      dispatch(logout());
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Still logout locally even if API call fails
      dispatch(logout());
    }
  }, [dispatch, navigate]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-20 bg-gray-600 bg-opacity-50 lg:hidden"
          onClick={() => dispatch(toggleSidebar())}
        />
      )}
      
      {/* Sidebar */}
      <div className={`${
        sidebarOpen 
          ? 'w-64 translate-x-0' 
          : 'w-16 -translate-x-full lg:translate-x-0'
      } fixed lg:static inset-y-0 left-0 z-30 bg-white shadow-lg transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className={`flex ${sidebarOpen ? 'items-start justify-start px-4 py-3' : 'items-center justify-center px-2 py-4'} min-h-16 border-b border-gray-200`}>
          {sidebarOpen ? (
            <div className="flex items-start space-x-3 min-w-0 w-full">
              {hospitalLogo ? (
                <img
                  src={hospitalLogo}
                  alt={displayName}
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain rounded-lg flex-shrink-0 mt-0.5"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white font-bold text-xs sm:text-sm">{displayName[0] || 'H'}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-sm sm:text-lg font-bold text-primary-600 leading-tight break-words hyphens-auto">
                  {displayName}
                </h1>
              </div>
            </div>
          ) : (
            <div className="w-8 h-8 flex-shrink-0">
              {hospitalLogo ? (
                <img
                  src={hospitalLogo}
                  alt={displayName}
                  className="h-8 w-8 object-contain rounded"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-8 h-8 bg-primary-600 rounded flex items-center justify-center">
                  <span className="text-white font-bold text-xs">{displayName[0] || 'H'}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto">
          <ul className="py-4 space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-primary-50 text-primary-600 border-r-4 border-primary-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } flex items-center px-4 py-2 text-sm font-medium transition-colors duration-150`}
                  >
                    <Icon className={`${sidebarOpen ? 'mr-3' : ''} h-5 w-5`} />
                    {sidebarOpen && <span>{item.name}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors duration-150 ${
              sidebarOpen ? 'justify-start' : 'justify-center'
            }`}
          >
            <LogOutIcon className={`${sidebarOpen ? 'mr-3' : ''} h-5 w-5`} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Navigation */}
        <header className="bg-white shadow-sm z-10">
          <div className="h-16 px-4 lg:px-6 flex items-center justify-between">
            {/* Left side - Menu button and Hospital Name */}
            <div className="flex items-center">
              <button
                onClick={() => dispatch(toggleSidebar())}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              >
                <MenuIcon className="h-6 w-6" />
              </button>
              
              {/* Show hospital name in header when sidebar is collapsed */}
              <div className="hidden lg:flex lg:items-center lg:ml-4">
                <span className="text-lg font-semibold text-gray-900">
                  {displayName}
                </span>
              </div>
            </div>

            {/* Right side - User menu and notifications */}
            <div className="flex items-center space-x-4">
              <NotificationsDropdown />
              
              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 focus:outline-none"
                >
                  <div className="hidden md:flex md:items-center md:space-x-2">
                    <span className="text-sm font-medium text-gray-700">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  </div>
                  <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                      <p className="font-medium">{user?.firstName} {user?.lastName}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout; 